/* Client for admin-created driver accounts on the PEAR Render backend.
   Creating a Firebase Auth user needs the admin SDK, so it runs server-side
   (the production replacement for the old "run a script locally" step). */
import { auth } from '../firebase';

const BASE = (process.env.REACT_APP_PAYMENTS_URL || '').replace(/\/$/, '');

export function isDriversApiConfigured(): boolean {
  return !!BASE;
}

export interface CreatedDriver {
  uid: string;
  email: string;
  password: string | null; // present only when a new account was created
  created: boolean;
  /** Did the driver land on the bus's roster? null when no bus was named.
   *  false means the account exists but buses/{id}.driverIds does not list them
   *  — and the trip routes authorise crew from that list alone (2026-09-16). */
  busLinked?: boolean | null;
}

export async function createDriver(args: { orgCode: string; email: string; displayName: string; busId?: string }): Promise<CreatedDriver> {
  if (!BASE) throw new Error('The backend is not configured (REACT_APP_PAYMENTS_URL).');
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('You appear to be signed out — please sign in again.');
  const res = await fetch(`${BASE}/api/driver/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(args),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) throw new Error(json.error || 'Could not create the driver account');
  return json as CreatedDriver;
}

/* ── Driver self sign-up (2026-09-16) ─────────────────────────────────────
   Decision A, "close it". firestore.rules used to let a brand-new account
   create its own users/{uid} = { role: 'bus_driver', schoolCode: 'VICTIM' },
   self-add to a victim bus and count as trip crew. The rules now refuse a
   client-created driver profile, and the backend mints every one instead:
   POST /api/driver/self-signup, called by the driver themselves on a verified
   account of their own. The join page and the three phone sign-up screens all
   build to this one wire contract. Nothing in here writes Firestore. */

export type DriverSignupProduct = 'school' | 'trasse' | 'cargo';
export type DriverSignupProof = { signupCode: string } | { inviteId: string };
export type DriverSignupVehicle =
  | { action: 'join'; plateOrNickname: string }
  | {
      action: 'create';
      plate: string;
      nickname: string;
      capacity?: number;
      vehicleType?: string;
      /** Product-specific vehicle fields the old client write carried (fuelType, busType…). */
      extra?: Record<string, unknown>;
    };

export interface DriverSelfSignupRequest {
  orgCode: string;
  product: DriverSignupProduct;
  proof: DriverSignupProof;
  name: string;
  phone?: string;
  dateOfBirth?: string; // YYYY-MM-DD
  vehicle: DriverSignupVehicle;
}

export interface DriverSelfSignupResult {
  ok: true;
  uid: string;
  role: string;
  productType: string;
  busId: string;
}

/** The input the server says to focus, when it names one. */
export type DriverSignupErrorField = 'phone' | 'plate' | 'signupCode' | 'name';

/** A refusal from /api/driver/self-signup. `message` is the server's sentence for the
 *  person and is shown verbatim; `code` is the machine reason (phone_in_use, bus_full,
 *  invite_invalid…); `field` names the input to send them back to. */
export class DriverSelfSignupError extends Error {
  code: string;
  field?: DriverSignupErrorField;
  status: number;

  constructor(message: string, opts: { code: string; field?: DriverSignupErrorField; status: number }) {
    super(message);
    // TS/Babel downlevel classes that extend Error lose their prototype; put it back so
    // `instanceof` is reliable wherever this is caught.
    Object.setPrototypeOf(this, DriverSelfSignupError.prototype);
    this.name = 'DriverSelfSignupError';
    this.code = opts.code;
    this.field = opts.field;
    this.status = opts.status;
  }
}

const SIGNUP_ERROR_FIELDS: ReadonlyArray<DriverSignupErrorField> = ['phone', 'plate', 'signupCode', 'name'];

/** Kenyan plate → canonical doc-id form (KAA 123 A → KAA123A). Same rule as the join
 *  page and the mobile app; a bus is keyed by this. */
export function normalizeDriverPlate(s: string): string {
  return (s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * Which of the three phone sign-up screens a driver of this org would have landed on —
 * the server checks the org's product against this. Mirrors the app's
 * OrganizationService.detectCodeType: the org document's productType decides
 * (BUS_ONLY / SELF_TRACKING → the Trasse screen, LORRY / CARGO → the cargo screen,
 * anything else → the school screen), and the code's suffix is only the fallback for
 * when the org could not be read.
 */
export function driverSignupProductFor(orgCode: string, orgProductType?: string | null): DriverSignupProduct {
  const pt = (orgProductType || '').trim().toUpperCase();
  if (pt === 'LORRY' || pt === 'CARGO') return 'cargo';
  if (pt === 'BUS_ONLY' || pt === 'SELF_TRACKING') return 'trasse';
  if (pt) return 'school';
  const c = (orgCode || '').trim().toLowerCase();
  if (c.endsWith('.lorry') || c.endsWith('.cargo') || c.endsWith('.fleet')) return 'cargo';
  if (c.endsWith('.bus')) return 'trasse';
  return 'school';
}

export interface DriverSelfSignupInput {
  orgCode: string;
  product: DriverSignupProduct;
  proof: DriverSignupProof;
  name: string;
  phone?: string;
  dateOfBirth?: string;
  /** As typed — normalised here, so the caller and the server agree on the doc id. */
  plate: string;
  /** true = join the bus with that plate; false = create it. */
  joining: boolean;
  seatCapacity?: string | number;
  fuelType?: string;
  vehicleType?: string;
}

/**
 * The request body for POST /api/driver/self-signup, built from what the join page (or
 * any other client) already holds. Pure, so it can be pinned by a test: join and create
 * carry exactly the fields the page used to write itself — `Bus <PLATE>` as the nickname,
 * seatCapacity only when it is a positive number, fuelType and busType under `extra`
 * (product-specific bus fields the server writes onto the bus doc as they are).
 *
 * A cargo org gets no bus type (2026-09-16): the page's picker only knows minibus |
 * large_bus, and the server would take that as the lorry's vehicleType, where the cargo
 * screen writes cargo | personal_track | general. Left out, the server applies the cargo
 * screen's own default.
 */
export function buildDriverSelfSignupRequest(input: DriverSelfSignupInput): DriverSelfSignupRequest {
  const plate = normalizeDriverPlate(input.plate);
  const name = (input.name || '').trim();
  const phone = (input.phone || '').trim();
  const dateOfBirth = (input.dateOfBirth || '').trim();
  const vehicleType = input.product === 'cargo' ? '' : (input.vehicleType || '').trim();
  const fuelType = (input.fuelType || '').trim();

  let vehicle: DriverSignupVehicle;
  if (input.joining) {
    vehicle = { action: 'join', plateOrNickname: plate };
  } else {
    const cap = typeof input.seatCapacity === 'number'
      ? input.seatCapacity
      : parseInt(String(input.seatCapacity ?? ''), 10);
    const extra: Record<string, unknown> = {};
    if (fuelType) extra.fuelType = fuelType;
    if (vehicleType) extra.busType = vehicleType;
    vehicle = {
      action: 'create',
      plate,
      nickname: `Bus ${plate}`,
      ...(Number.isFinite(cap) && cap > 0 ? { capacity: cap } : {}),
      ...(vehicleType ? { vehicleType } : {}),
      ...(Object.keys(extra).length ? { extra } : {}),
    };
  }

  return {
    orgCode: (input.orgCode || '').trim(),
    product: input.product,
    proof: input.proof,
    name,
    ...(phone ? { phone } : {}),
    ...(dateOfBirth ? { dateOfBirth } : {}),
    vehicle,
  };
}

/**
 * Mint the signed-in driver's own profile through the backend. Resolves with the
 * server's { uid, role, productType, busId }; rejects with a DriverSelfSignupError
 * carrying the server's sentence, code and field on any refusal, or a plain Error
 * when the call could not be made at all.
 */
export async function selfSignupDriver(body: DriverSelfSignupRequest): Promise<DriverSelfSignupResult> {
  if (!BASE) throw new Error('The backend is not configured (REACT_APP_PAYMENTS_URL).');
  // Forced mint: the server insists on email_verified in the token, and a token cached
  // from before the email-link sign-in would not carry it.
  const token = await auth.currentUser?.getIdToken(true);
  if (!token) throw new Error('You appear to be signed out — please sign in again.');
  const res = await fetch(`${BASE}/api/driver/self-signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const json = (await res.json().catch(() => ({}))) as Partial<Omit<DriverSelfSignupResult, 'ok'>> & {
    ok?: boolean; error?: string; code?: string; field?: string;
  };
  if (!res.ok || json.ok === false) {
    const field = SIGNUP_ERROR_FIELDS.find(f => f === json.field);
    throw new DriverSelfSignupError(json.error || 'Could not set up your driver account. Please try again.', {
      code: json.code || `http_${res.status}`,
      field,
      status: res.status,
    });
  }
  return json as DriverSelfSignupResult;
}

/* A parent's school membership, proven by the server from the invite (2026-09-17).
 * Role 'parent' and its org are self-declared, so the bus / lorry roster rules
 * require orgMembers/{org}__{uid}, which only the backend writes
 * (pear-payments-backend/src/parentMembership.js). FleetJoinPage claims it from
 * the invite BEFORE writing the parent's account or any roster. Never throws:
 * `error` is the server's sentence, or a could-not-reach one. */
export async function claimParentMembership(inviteId: string): Promise<{ ok: true } | { ok: false; error: string }> {
  const unreachable = { ok: false as const, error: 'Could not confirm your invite right now. Check your connection and try again.' };
  if (!BASE) return unreachable;
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return { ok: false, error: 'You appear to be signed out — please sign in again.' };
    const res = await fetch(`${BASE}/api/parent/membership/invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ inviteId }),
    });
    const json = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
    if (res.ok && json.ok === true) return { ok: true };
    if (res.status >= 400 && res.status < 500 && typeof json.error === 'string') return { ok: false, error: json.error };
    return unreachable;
  } catch {
    return unreachable;
  }
}

/* A co-parent's link to the primary parent, recorded by the server from the primary's
 * co-parent code (2026-09-17). The roster rules let a parent change only their own
 * children's rows — or, once this is recorded, the primary parent's. The caller must
 * already be a member of the school (claimParentMembership). Never throws. */
export async function linkCoParentMembership(code: string): Promise<
  { ok: true; primaryParentId: string } | { ok: false; error: string }
> {
  const unreachable = { ok: false as const, error: 'Could not link you to the family right now. Check your connection and try again.' };
  if (!BASE) return unreachable;
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return { ok: false, error: 'You appear to be signed out — please sign in again.' };
    const res = await fetch(`${BASE}/api/parent/membership/coparent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ code }),
    });
    const json = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string; primaryParentId?: string };
    if (res.ok && json.ok === true && typeof json.primaryParentId === 'string') {
      return { ok: true, primaryParentId: json.primaryParentId };
    }
    if (res.status >= 400 && res.status < 500 && typeof json.error === 'string') return { ok: false, error: json.error };
    return unreachable;
  } catch {
    return unreachable;
  }
}

/* The join page's "buses at your school" list (2026-09-17). Bus reads are limited to
 * a vehicle's own people, and an invitee — above all a driver, whose profile the
 * backend has not minted yet — is none of them, so the page asks the server, which
 * checks the invite and returns plates and names only. Never throws: [] on failure. */
export async function fetchInviteBusPlates(inviteId: string): Promise<Array<{ id: string; plate: string; name: string }>> {
  if (!BASE || !inviteId) return [];
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return [];
    const res = await fetch(`${BASE}/api/fleet/invite/buses`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ inviteId }),
    });
    const json = (await res.json().catch(() => ({}))) as { ok?: boolean; buses?: Array<{ id: string; plate: string; name: string }> };
    return res.ok && json.ok === true && Array.isArray(json.buses) ? json.buses : [];
  } catch {
    return [];
  }
}
